import type { Quiz } from "./types";

/**
 * The one people forward.
 *
 * "Which bible character are you" is searched enormously and served almost
 * entirely by pages that pick a name at random and pay you a compliment. The
 * whole of the opportunity is in refusing to do that: six people who are
 * actually in the text, described with what the text says about them including
 * the parts nobody puts on a mug, and a result that ends in something to do.
 */
export const article: Quiz = {
  slug: "which-bible-character-are-you",
  kind: "reflection",
  title: "Which Bible character are you? A test that is not flattery",
  h1: "Which Bible character are you?",
  description:
    "Ten questions, six people from scripture, and results that include the parts nobody embroiders on a cushion. Peter was not only brave and Martha was not only busy.",
  targets: [
    "which bible character are you quiz",
    "bible personality test",
    "what biblical figure am i like",
  ],
  published: "2026-08-26",
  standfirst:
    "Most quizzes like this hand you a hero and a compliment. The people in scripture are more interesting than that, and so, probably, are you.",
  takeaways: [
    "The six people here are Ruth, Barnabas, Martha, Nehemiah, Thomas and Peter — chosen because each one has a documented strength that is also, from a slightly different angle, their documented failure.",
    "Scripture is unusually honest about its own leading figures: Peter denies, Martha complains, Thomas refuses to believe his closest friends, and none of it is edited out.",
    "The result is a description of a tendency, not a claim about your soul, and the useful half is the paragraph about where that tendency costs you something.",
    "Every outcome ends with three concrete practices, because the point of recognising a pattern is doing something differently on Tuesday.",
  ],
  intro: [
    {
      type: "p",
      text: "The problem with most quizzes of this kind is not that they are unserious. It is that they are flattering, and flattery is the opposite of what the Bible does with its own characters. Peter is the one who walks on water and the one who swears at a servant girl that he has never met the man. Martha runs the house that Jesus is glad to be in and is told, gently, that she has got the evening wrong. Thomas says the thing everybody else was thinking and gets his name attached to it for two thousand years.",
    },
    {
      type: "p",
      text: "That honesty is what makes the exercise worth ten minutes. So each result below has three parts: what this tendency is good for, where it costs you, and what to do about it. The questions take about four minutes and the result updates as you answer.",
    },
    {
      type: "note",
      text: "Six people, none of them a hero and none of them a warning. Scripture keeps the whole of a person on the page, which is more than most of us do for ourselves.",
    },
  ],
  questions: [
    {
      id: "friday",
      prompt: "It is Friday evening and a friend rings in real trouble.",
      options: [
        {
          text: "I am in the car. We can work out what to say when I get there.",
          scores: { peter: 3, ruth: 2 },
        },
        {
          text: "I ask what they need, then I organise it — food, a bed, whoever else should know.",
          scores: { nehemiah: 3, martha: 2 },
        },
        {
          text: "I listen for an hour and say almost nothing, which is what they wanted.",
          scores: { barnabas: 3, ruth: 1 },
        },
        {
          text: "I ask questions until I understand what is actually going on.",
          scores: { thomas: 3 },
        },
      ],
    },
    {
      id: "church",
      prompt: "At church, where are you?",
      options: [
        {
          text: "In the kitchen, or the car park, or both.",
          scores: { martha: 3, nehemiah: 1 },
        },
        {
          text: "Next to whoever came in alone and looked like they might leave.",
          scores: { barnabas: 3 },
        },
        {
          text: "Somewhere near the back, thinking about a line in the sermon I am not sure about.",
          scores: { thomas: 3 },
        },
        {
          text: "Wherever I was last week, with the same people. I do not move much.",
          scores: { ruth: 3 },
        },
      ],
    },
    {
      id: "doubt",
      prompt: "Something you were taught stops making sense to you.",
      options: [
        {
          text: "I say so, out loud, probably at the wrong moment.",
          scores: { thomas: 3, peter: 1 },
        },
        {
          text: "I sit with it quietly for a long time and tell almost nobody.",
          scores: { ruth: 2, thomas: 1 },
        },
        {
          text: "I keep going. The doing does not stop because the thinking is unfinished.",
          scores: { martha: 2, nehemiah: 2 },
        },
        {
          text: "I find the person most likely to understand and ask them.",
          scores: { barnabas: 2, thomas: 1 },
        },
      ],
    },
    {
      id: "project",
      prompt: "Something big needs doing and nobody has started it.",
      options: [
        { text: "I start it, badly, today.", scores: { peter: 3 } },
        {
          text: "I make a list, work out who is needed, and start on Monday.",
          scores: { nehemiah: 3 },
        },
        {
          text: "I do the part in front of me and keep doing it until it is done.",
          scores: { martha: 2, ruth: 2 },
        },
        {
          text: "I find the person who should lead it and back them.",
          scores: { barnabas: 3 },
        },
      ],
    },
    {
      id: "criticism",
      prompt: "Someone criticises you in front of other people.",
      options: [
        {
          text: "I answer immediately and regret half of it.",
          scores: { peter: 3 },
        },
        {
          text: "I say nothing and think about it for three days.",
          scores: { ruth: 2, thomas: 2 },
        },
        {
          text: "I get on with the task. It can be dealt with later.",
          scores: { nehemiah: 2, martha: 2 },
        },
        {
          text: "I find them afterwards and ask what they meant.",
          scores: { barnabas: 2, thomas: 1 },
        },
      ],
    },
    {
      id: "help",
      prompt: "A person everyone else has given up on asks you for a chance.",
      options: [
        {
          text: "They get one. That is not a difficult question.",
          scores: { barnabas: 3 },
        },
        {
          text: "They get one, with a plan attached and someone checking in.",
          scores: { nehemiah: 2, barnabas: 1 },
        },
        {
          text: "I want to know what changed before I answer.",
          scores: { thomas: 3 },
        },
        {
          text: "I stay, quietly, whatever the answer turns out to be.",
          scores: { ruth: 3 },
        },
      ],
    },
    {
      id: "tired",
      prompt: "You are exhausted and there is still work in the room.",
      options: [
        {
          text: "I do it, and I am not gracious about who is not helping.",
          scores: { martha: 3 },
        },
        {
          text: "I do it, and say nothing, and do it again tomorrow.",
          scores: { ruth: 2, martha: 1 },
        },
        {
          text: "I stop, and mind less than most people would.",
          scores: { peter: 1, thomas: 1 },
        },
        {
          text: "I get other people to do it with me. That is most of the skill.",
          scores: { nehemiah: 3 },
        },
      ],
    },
    {
      id: "promise",
      prompt: "You said you would do something. It has become inconvenient.",
      options: [
        { text: "I do it. I said I would.", scores: { ruth: 3, martha: 1 } },
        {
          text: "I do it and everyone hears how inconvenient it was.",
          scores: { martha: 2, peter: 1 },
        },
        {
          text: "I renegotiate early rather than fail late.",
          scores: { nehemiah: 2, thomas: 1 },
        },
        {
          text: "I meant it completely when I said it, which is not the same as doing it.",
          scores: { peter: 3 },
        },
      ],
    },
    {
      id: "newperson",
      prompt: "A new person joins the group and nobody has spoken to them.",
      options: [
        { text: "I do, immediately.", scores: { barnabas: 3, peter: 1 } },
        {
          text: "I make sure they have something to do. That is easier than talking.",
          scores: { martha: 2, nehemiah: 1 },
        },
        {
          text: "I notice, and hope somebody else does, and feel bad about it later.",
          scores: { thomas: 1, ruth: 1 },
        },
        {
          text: "I sit with them and stay sat there for the next six months.",
          scores: { ruth: 3 },
        },
      ],
    },
    {
      id: "faith",
      prompt:
        "Which sentence is closest to your faith on an ordinary Wednesday?",
      options: [
        {
          text: "Loud, certain, and occasionally embarrassing to me later.",
          scores: { peter: 3 },
        },
        {
          text: "Mostly expressed as work — meals, lifts, the rota.",
          scores: { martha: 3, nehemiah: 1 },
        },
        {
          text: "Quiet, stubborn, and mostly about not leaving.",
          scores: { ruth: 3 },
        },
        {
          text: "Honest about what I do not know, which some people find unhelpful.",
          scores: { thomas: 3 },
        },
      ],
    },
  ],
  outcomes: [
    {
      id: "ruth",
      name: "Ruth — the one who stays",
      summary:
        "You are not the loudest person in any room and you are the one still there in March. Loyalty is your whole method, and it is rarer than charisma.",
      body: [
        "Ruth's famous line is spoken to a bitter, grieving mother-in-law who has just told her twice to go home. There is no crowd, no strategic upside and no obvious reason for it. That is the shape of the tendency your answers describe: you stay, and you keep the promise after the feeling that produced it has gone, and you do it without narrating it.",
        "The cost is that people stop noticing. Steadiness is invisible in a way that effort is not — nobody thanks the beam for holding the roof up — and quiet people who keep showing up are quietly relied on until something breaks. You are also, by disposition, slow to say when you have had enough, which means when you finally say it, it lands as a shock rather than as news.",
        "The other risk is smaller and worth naming: staying can become the answer to every question, including the ones where the right answer is to go. Faithfulness is a decision made repeatedly, not a groove you fall into.",
      ],
      verse: {
        text: "Where you go, I will go; and where you stay, I will stay.",
        ref: "Ruth 1:16",
      },
      practice: [
        "Say one thing you need out loud this week, to one person, before it becomes a grievance.",
        "Write down the two commitments you are actually keeping and the four you are half-keeping out of habit, and end one of the four.",
        "Let someone thank you properly instead of deflecting it. Deflection is a way of staying invisible.",
      ],
    },
    {
      id: "barnabas",
      name: "Barnabas — the one who vouches",
      summary:
        "Your instinct is to back people, particularly the ones everyone else has written off. Half the New Testament exists because someone did that for Paul, and it was this man.",
      body: [
        "Barnabas gets a nickname in Acts that means son of encouragement, which sounds like faint praise until you notice what he actually did with it. He vouched for a recent persecutor of the church when nobody in Jerusalem would go near him, and later he split with Paul over giving a second chance to a young man who had walked out on them. He lost the argument and took the young man anyway. That is not softness; that is a costly conviction about people.",
        "Your answers describe the same reflex — you make room, you introduce, you notice the person nobody has spoken to. It is the least glamorous form of leadership and it produces more than the glamorous kind — most of what any church actually does rests on two or three people with this instinct, none of whom appear anywhere in the account of it.",
        "The cost comes in two forms. You will be wrong about people sometimes, publicly, and it will be held against you. And encouragers are chronically underweighted in their own accounting: you can spend years making other people possible and never ask what you were meant to be doing, which is a real loss and not a noble one.",
      ],
      verse: {
        text: "He was a good man, and full of the Holy Spirit and of faith.",
        ref: "Acts 11:24",
      },
      practice: [
        "Name the thing you would be doing if you were not busy making other people possible, and give it one hour a week.",
        "Back somebody in writing this month — a recommendation, an introduction, a public word. Verbal encouragement evaporates.",
        "Find the person who does this for you. If the answer is nobody, that is the thing to fix first.",
      ],
    },
    {
      id: "martha",
      name: "Martha — the one who does it",
      summary:
        "Things happen because you do them. The rota, the meal, the lift, the thing nobody else remembered — and the resentment that arrives on schedule at about nine o'clock.",
      body: [
        "The story everyone remembers is the one where Martha gets it wrong, and it is worth reading again, because Jesus is not criticising the work. He is in her house, eating her food, and later he raises her brother after she says one of the clearest confessions of faith in the gospels. She is not the cautionary tale she has been turned into. What she gets told is that she is worried and troubled about many things, which is a diagnosis rather than a rebuke.",
        "Your answers show the same engine and the same failure mode. The work is real service and it is genuinely load-bearing for whatever community you are in. But it becomes a way of being present without being present, and it comes with an invoice — you notice exactly who is not helping, and the noticing curdles.",
        "The specific correction is not to do less. It is that some part of the week has to be spent as a guest rather than as staff, including in your own house and including at church. Not as a reward once the list is clear, either, because the list is never clear and was never going to be.",
      ],
      verse: {
        text: "Martha, Martha, you are anxious and troubled about many things, but one thing is needed.",
        ref: "Luke 10:41–42",
      },
      practice: [
        "Sit down for the whole of one meal you would normally be serving. Somebody else can carry the plates badly.",
        "Take one thing off your list permanently — not delegate, remove — and see whether the sky falls.",
        "When the resentment arrives, say the actual sentence to the actual person, early, in plain words.",
      ],
    },
    {
      id: "nehemiah",
      name: "Nehemiah — the one who builds it",
      summary:
        "You see a broken thing, work out what it would take, and get other people to carry it with you. Walls do not go up because someone felt strongly about walls.",
      body: [
        "Nehemiah hears that a city is in ruins, weeps, prays, and then asks the king for timber, letters of passage and a date of return. The praying and the logistics are the same act, told in the same breath, which is unusual in scripture and exactly right. He surveys the damage at night before he says a word about a plan, and when the work starts he puts families in front of the sections of wall nearest their own houses, because he understood what people will actually turn up for.",
        "Your answers describe that instinct: not the person who starts things impulsively, but the one who makes them finishable. It is the rarest of the six and the one most likely to be handed everything, which is the first cost: capable organisers accumulate responsibility by default, and nobody involved ever notices that the accumulation was a decision somebody made.",
        "Two costs. Builders can mistake the structure for the point — the rota becomes the ministry, the process becomes the mission. And Nehemiah's own account has a hardness in it, an impatience with people who are slower or more compromised than he is, that the text does not smooth over. Efficiency is not a fruit of the Spirit, and it makes an excellent hiding place for a lack of one.",
      ],
      verse: {
        text: "Come, let's build up the wall of Jerusalem, that we won't be disgraced.",
        ref: "Nehemiah 2:17",
      },
      practice: [
        "Ask, of the biggest thing you are running: if this were finished perfectly, what would actually be better for whom?",
        "Give one piece of it away entirely — decisions included — to someone who will do it differently and worse for a while.",
        "Keep a day where nothing is being organised. Builders rest badly and rarely think it is a problem.",
      ],
    },
    {
      id: "thomas",
      name: "Thomas — the one who asks",
      summary:
        "You say the thing everyone else is thinking and would rather not say. It has cost you socially and it is the reason people trust what you affirm.",
      body: [
        "Thomas has been badly served by his nickname. His actual recorded lines are these: at the news that Jesus is going back to hostile territory, he says let us also go, that we may die with him — that is the doubter — and later, in the upper room, he simply refuses to say he believes something he has not seen, while ten friends insist. He is offered the evidence and does not take it. He answers with the highest confession in John's gospel.",
        "Your answers show that same combination: unwilling to perform certainty, and considerably braver than the people who find you difficult. Honest questions are load-bearing in a church, because they are the only thing that makes the affirmations mean anything.",
        "The cost is that scepticism is comfortable. It carries no obligation, and it can become a way of never having to commit to anything, which is not integrity but a very good imitation of it. It is also isolating: ten people had already told Thomas the truth and he preferred to wait alone.",
      ],
      verse: {
        text: "Thomas answered him, “My Lord and my God!”",
        ref: "John 20:28",
      },
      practice: [
        "Take one question you have been circling for a year and give it thirty minutes with someone who has actually studied it, rather than another lap alone.",
        "Act on something you are ninety per cent sure of instead of waiting for a hundred. Waiting is also a decision.",
        "Notice when a question is a genuine question and when it is a way of staying out of the room.",
      ],
    },
    {
      id: "peter",
      name: "Peter — the one who goes first",
      summary:
        "You are out of the boat before you have thought about the water. It is why people follow you and why you have things to apologise for.",
      body: [
        "Peter is the one who says the right answer and is called Satan four verses later. He swings a sword in a garden, swears he does not know the man before the night is over, and swims to shore when he sees him again on the beach rather than waiting for the boat. Nothing about him is measured, and the church is built on him anyway.",
        "Your answers describe the same pattern: the immediate yes, the arriving before you know what you will say, the sincere promise made in a moment that is genuinely meant and is not the same thing as kept. It is a real gift. Somebody has to go first, and most people never do.",
        "The cost is a trail of overcommitments and a habit of leading with volume. Fast people are also the last to notice when their confidence is doing the talking. What is worth taking from the story is the ending rather than the failure: the restoration on the beach is three questions and a job, not an inquest. Whatever you have got wrong at speed can be repaired at speed too.",
      ],
      verse: {
        text: "Feed my sheep.",
        ref: "John 21:17",
      },
      practice: [
        "Put twenty-four hours between yes and any commitment longer than a month.",
        "Go back to the last thing you promised and dropped and either do it or say plainly that you will not.",
        "Learn one thing slowly this year, on purpose, with no audience.",
      ],
    },
  ],
  after: [
    {
      type: "h2",
      id: "why-these-six",
      text: "Why these six",
    },
    {
      type: "p",
      text: "Because each of them has a strength that is also, from a slightly different angle, the thing that went wrong. Ruth's loyalty and Ruth's invisibility are one trait. Peter's speed and Peter's denial are one trait. That is the shape of an actual person, and it is what a quiz that only tells you which hero you are will always miss.",
    },
    {
      type: "p",
      text: "It is also what makes the exercise useful rather than entertaining. Knowing you tend toward Martha does not mean stopping the work; it means noticing the hour of the evening when the work turns into a grudge. Knowing you tend toward Thomas does not mean less questioning; it means noticing which question is real. If you want the same honesty pointed at your phone instead of your temperament, {the-phone-and-faith-test} is twelve questions in the same spirit.",
      links: { "the-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
    {
      type: "h2",
      id: "reading-the-passages",
      text: "Reading the passages themselves",
    },
    {
      type: "p",
      text: "Each result names a passage, and the descriptions above are compressed to a few hundred words each. The originals are better. Ruth is four short chapters and can be read in twenty minutes; the restoration of Peter is the last chapter of John; Nehemiah's night survey of the walls is in chapter two. Bible Shield ships the whole text with search and highlights, and the same app closes the feed apps for the twenty minutes it takes to read a book of it.",
    },
    {
      type: "note",
      text: "A tendency is not a verdict. The most interesting thing you can do with a result like this is to ask which of the other five you have needed and not been.",
    },
  ],
  faq: [
    {
      q: "Is there a right answer to this quiz?",
      a: "No. There are no correct options and no scoring against a standard — the questions weight toward six tendencies and the heaviest one wins. Answer as you actually behave rather than as you would like to, or the result describes somebody else.",
    },
    {
      q: "Why are there only six characters?",
      a: "Because six results each with real writing behind them is more useful than twenty with a sentence apiece, and because these six cover the ground: the one who stays, the one who backs people, the one who does the work, the one who builds the structure, the one who asks, and the one who goes first.",
    },
    {
      q: "Which translation are the verses from?",
      a: "The World English Bible, which is in the public domain. Bible Shield ships several translations in the app if you want to compare a passage.",
    },
    {
      q: "Are my answers stored?",
      a: "No. The quiz runs entirely in your browser, nothing is sent to a server, and closing the page ends it. Sharing a result shares the name of the result and nothing else.",
    },
  ],
  related: [
    "morning-quiet-time-routine",
    "bible-in-a-year-on-your-phone",
    "digital-sabbath",
  ],
  relatedQuizzes: ["prayer-style-quiz", "bible-knowledge-quiz"],
};
